/**
 * Search tracking event handlers and setup functions.
 * Provides functionality for tracking search interactions and managing event listeners.
 * @module tracking-handlers
 */

import { debounce } from '@remarkablenet/rcm-utils/debounce';
import type { TrackingConfiguration } from './tracking-config';
import type { SearchTrackingEvent } from '../types/tracking-config';
import { logDebug } from './tracking-logger';
import type { SearchElements } from '../types/search-elements';
import { findSearchContainer, findShowAllResultsLink, extractSearchTerm } from './dom-utils';

/** State tracking for search panel open/close status */
let isSearchPanelOpen = false;

/** Map to store event listeners and original property descriptors for cleanup */
const inputListenerMap = new WeakMap<HTMLInputElement, {
  input: (e: Event) => void;
  keydown: (e: KeyboardEvent) => void;
  originalDescriptor?: PropertyDescriptor;
}>();

/** Map to store show all results click handler for cleanup */
const showAllResultsListenerMap = new WeakMap<HTMLElement, (e: Event) => void>();

/**
 * Sets up event listeners for the search toggle button with state check
 * @param element - The DOM element to attach listeners to
 * @param clickHandler - Function to handle click events
 * @param keyHandler - Function to handle keydown events
 * @returns void
 * @throws Error if element is null
 */
export const setupSearchToggleListeners = (
  element: HTMLElement,
  clickHandler: (e: Event) => void,
  keyHandler: (e: KeyboardEvent) => void
): void => {
  // Wrapped to check for isSearchPanelOpen state
  const wrappedClickHandler = (e: Event) => {
    if (isSearchPanelOpen) {
      logDebug("Search panel already open, ignoring click");
      return;
    }
    isSearchPanelOpen = true;
    clickHandler(e);
  };

  // Wrapped to check for isSearchPanelOpen state
  const wrappedKeyHandler = (e: KeyboardEvent) => {
    if (isSearchPanelOpen) {
      logDebug("Search panel already open, ignoring keypress");
      return;
    }
    isSearchPanelOpen = true;
    keyHandler(e);
  };

  element.addEventListener("click", wrappedClickHandler);
  element.addEventListener('keydown', wrappedKeyHandler);
  logDebug("Search Toggle Overlay listeners attached");
};

/**
 * Removes search toggle event listeners and resets state
 * @returns void
 */
export const removeSearchToggleListeners = (): void => {
  logDebug("Search Toggle Overlay listeners detached");
};

/**
 * Handles the opening of the search panel and initializes search functionality
 * @param e - The triggering event
 * @param allowDefault - Whether to allow default event behavior
 * @param initializeInputs - Callback function to initialize search inputs
 * @param initDelay - Delay in milliseconds before initializing inputs
 * @returns void
 * @throws Error if event handling fails
 */
export const handleSearchPanelOpen = (
  e: Event,
  allowDefault = false,
  initializeInputs?: () => void,
  initDelay?: number
): void => {
  try {
    const event = { event: "search_opened" };
    window.dataLayer.push(event);
    logDebug("DataLayer Event Pushed:", event);

    if (initializeInputs && initDelay) {
      setTimeout(initializeInputs, initDelay);
    }

    if (allowDefault && e.target instanceof HTMLElement) {
      const newEvent = new Event(e.type, {
        bubbles: e.bubbles,
        cancelable: e.cancelable
      });
      e.target.dispatchEvent(newEvent);
    }
  } catch (error) {
    logDebug("Error in handleSearchPanelOpen:", error);
  }
};

/**
 * Handles click events for the search toggle
 * @param e - The click event
 * @param initializeInputs - Function to initialize search inputs
 * @param initDelay - Delay before initializing inputs
 */
export const handleSearchToggleClick = (
  e: Event,
  initializeInputs: () => void,
  initDelay: number
): void => {
  e.preventDefault();
  removeSearchToggleListeners();
  handleSearchPanelOpen(e, true, initializeInputs, initDelay);
};

/**
 * Handles keydown events for the search toggle
 * @param e - The keydown event
 * @param initializeInputs - Function to initialize search inputs
 * @param initDelay - Delay before initializing inputs
 */
export const handleSearchToggleKeyDown = (
  e: KeyboardEvent,
  initializeInputs: () => void,
  initDelay: number
): void => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    removeSearchToggleListeners();
    handleSearchPanelOpen(e, true, initializeInputs, initDelay);
  }
};

/**
 * Finds and returns search inputs and their container elements based on configuration
 * @param tracking - The tracking configuration object
 * @returns Object containing arrays of search inputs and container elements
 */
export const getSearchElements = (tracking: TrackingConfiguration): SearchElements => {
  const searchInputs: HTMLInputElement[] = [];
  const containerElements: HTMLElement[] = [];

  if (!tracking.containers || tracking.containers.length === 0) {
    logDebug("No containers configured");
    return { searchInputs, containerElements };
  }

  for (const container of tracking.containers) {
    const containerEl = document.querySelector<HTMLElement>(container.selectors.container);
    if (!containerEl) {
      logDebug(`Container not found for selector: ${container.selectors.container}`);
      continue;
    }

    let input: HTMLInputElement | null = null;
    if (container.selectors.iframeSelector) {
      const iframe = containerEl.querySelector<HTMLIFrameElement>(container.selectors.iframeSelector);
      if (iframe?.contentWindow?.document) {
        input = iframe.contentWindow.document.querySelector(container.selectors.searchInput);
      }
    } else {
      input = containerEl.querySelector(container.selectors.searchInput);
    }

    if (input) {
      searchInputs.push(input);
      containerElements.push(containerEl);
      logDebug(`Found search input for provider: ${container.providerId}`);
    }
  }
  logDebug("Search Inputs Found:", searchInputs);
  logDebug("Tracking:", tracking);
  return { searchInputs, containerElements };
};

/**
 * Determines the provider ID for a given element based on its container or Iframe element
 * @param element - The DOM element to check
 * @param tracking - The tracking configuration object
 * @returns The provider ID string
 */
export const getProviderForElement = (element: HTMLElement, tracking: TrackingConfiguration): string => {
  for (const container of tracking.containers) {
    const containerSelector = container.selectors.container;
    if (containerSelector !== "" && containerSelector !== undefined) {
      const containerEl: HTMLElement | null = document.querySelector<HTMLElement>(containerSelector);
      if (containerEl?.contains(element) || false) {
        return container.providerId;
      }

      const iframeSelector = container.selectors.iframeSelector;
      if (iframeSelector && containerEl) {
        const iframeEl: HTMLIFrameElement | null = containerEl.querySelector<HTMLIFrameElement>(iframeSelector);
        if (iframeEl?.contentWindow?.document.querySelector<HTMLElement>(element.tagName.toLowerCase()) !== null) {
          return container.providerId;
        }
      }
    }
  }
  logDebug("No provider found for the element. Returning default provider.");
  return tracking.containers[0]?.providerId || 'BLOOMREACH';
};

/**
 * Sets up search input tracking with debounced event handling
 * This function tracks search events based on user input, by listening to input and keydown events, 
 * and mutations on the input value
 * @param inputs - Array of input elements to track
 * @param tracking - The tracking configuration object
 */
export const setupSearchInputListeners = (inputs: HTMLInputElement[], tracking: TrackingConfiguration): void => {
  const debouncedTrackSearch = debounce((value: string, element: HTMLElement) => {
    if (!value) return;
    const provider = getProviderForElement(element, tracking);
    const providerConfig = tracking.trackingTypes[provider];
    if (!providerConfig?.events || !providerConfig.parameters) return;

    const event: SearchTrackingEvent = {
      event: providerConfig.events.searchTracking,
      [providerConfig.parameters.searchTerm]: value
    };

    window.dataLayer.push(event);
    logDebug("DataLayer Event Pushed:", event);

  }, tracking.searchConfig.typingInterval);

  for (const input of inputs) {

    const inputHandler = () => {
      debouncedTrackSearch(input.value.trim(), input);
    };
    const keydownHandler = () => {
      debouncedTrackSearch(input.value.trim(), input);
    };

    input.addEventListener("keydown", keydownHandler, { capture: true, passive: true });
    input.addEventListener("input", inputHandler, { capture: true, passive: true });

    // Get original descriptor to observe its setter
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    if (!descriptor) return;

    // Store original setter
    const originalSet = descriptor.set;

    // Create new descriptor that tracks changes but doesn't interfere
    Object.defineProperty(input, 'value', {
      ...descriptor,
      set: function (this: HTMLInputElement, val: string) {
        if (originalSet) {
          originalSet.call(this, val);
        }
        logDebug("Value changed:", val);
        debouncedTrackSearch(val.trim(), this);
      }
    });

    // Store for cleanup
    inputListenerMap.set(input, {
      input: inputHandler,
      keydown: keydownHandler,
      originalDescriptor: descriptor
    });
  }
};

export const cleanupSearchInputListeners = (input: HTMLInputElement): void => {
  const stored = inputListenerMap.get(input);
  if (!stored) return;

  // Remove event listeners
  input.removeEventListener("input", stored.input, { capture: true });
  input.removeEventListener("keydown", stored.keydown, { capture: true });

  // Restore original descriptor
  if (stored.originalDescriptor) {
    Object.defineProperty(input, 'value', stored.originalDescriptor);
  }

  inputListenerMap.delete(input);
  isSearchPanelOpen = false;
  logDebug("Cleaned up input value observer and listeners");
};

/**
 * Sets up mutation observers for search container elements
 * This is used to detect when the search panel is closed (IFrame or modal)
 * @param containers - Array of container elements to observe
 * @param tracking - The tracking configuration object
 * @param onClose - Callback function to execute when search is closed
 */
export const setupContainerObserver = (
  containers: HTMLElement[],
  tracking: TrackingConfiguration,
  onClose: () => void
): void => {
  for (const container of containers) {
    if (!container) continue;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (!target.classList.contains('is-active')) {
            const event = { event: "search_closed" };
            window.dataLayer.push(event);
            logDebug("DataLayer Event Pushed:", event);

            logDebug('Search panel closed - class removed');
            onClose();
            observer.disconnect();
            break;
          }
        }

        if (mutation.type === 'childList' && container.children.length === 0) {
          const event = { event: "search_closed" };
          window.dataLayer.push(event);
          logDebug("DataLayer Event Pushed:", event);

          logDebug('Search panel closed');
          onClose();
          observer.disconnect();
          break;
        }
      }
    });

    observer.observe(container, {
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }
};

/**
 * Sets up event listeners for the show all results link on the search,
 * before the search results page is loaded
 * @param tracking - The tracking configuration object
 */
export const setupShowAllResultsListeners = (tracking: TrackingConfiguration): void => {
  const searchContainer = findSearchContainer();
  if (!searchContainer) return;

  const clickHandler = (e: Event) => {
    const target = e.target as HTMLElement;
    const showAllResultsLink = findShowAllResultsLink(target);
    if (!showAllResultsLink) return;

    const searchTerm = extractSearchTerm(showAllResultsLink);
    const provider = getProviderForElement(target, tracking);
    const trackingEvents = tracking.trackingTypes[provider as keyof typeof tracking.trackingTypes];

    if (trackingEvents?.events?.allResultsClicked && trackingEvents?.parameters?.searchTerm) {
      const event = {
        event: trackingEvents.events.allResultsClicked,
        [trackingEvents.parameters.searchTerm]: searchTerm
      };
      window.dataLayer.push(event);
      logDebug("DataLayer Event Pushed:", event);
    }
  };

  // Store the handler for cleanup
  showAllResultsListenerMap.set(searchContainer, clickHandler);
  searchContainer.addEventListener('click', clickHandler);
};

/**
 * Cleans up all search related event listeners and observers
 * @param input - The input element to clean up
 * @param searchContainer - The search container element
 */
export const cleanupSearchEventListeners = (input: HTMLInputElement, searchContainer: HTMLElement | null): void => {
  // Clean up input listeners
  cleanupSearchInputListeners(input);

  // Clean up show all results listener
  if (searchContainer) {
    const showAllResultsHandler = showAllResultsListenerMap.get(searchContainer);
    if (showAllResultsHandler) {
      searchContainer.removeEventListener('click', showAllResultsHandler);
      showAllResultsListenerMap.delete(searchContainer);
      logDebug("Cleaned up show all results listener");
    }
  }
};
